import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'store/hooks';
import {
  mediaDevicesUpdated,
  selectActiveMediaDevices,
  selectAllMediaDevicePermissions,
  selectMediaDevices,
  selectMediaDevicesByKind,
} from 'features/user-media/userMediaSlice';
import { enumerateDevices } from 'features/user-media/utils';
import { EnumeratedMediaDevices, UpdateMediaDevicesMeta } from 'features/user-media/types';
import { safeLocalStorage } from 'utils/storage';

export interface UpdateMediaDevicesFunction {
  (meta?: UpdateMediaDevicesMeta): Promise<EnumeratedMediaDevices>;
}

export const useMediaDevices = () => {
  const dispatch = useDispatch();

  const mediaDevices = useAppSelector(selectMediaDevicesByKind);
  const mediaDeviceEntities = useAppSelector(selectMediaDevices);
  const activeMediaDevices = useAppSelector(selectActiveMediaDevices);
  const permissions = useAppSelector(selectAllMediaDevicePermissions);

  const updateMediaDevices: UpdateMediaDevicesFunction = useCallback(
    async (meta?: UpdateMediaDevicesMeta) => {
      const updatedDevices = await enumerateDevices();
      dispatch(mediaDevicesUpdated(updatedDevices, meta));
      return updatedDevices;
    },
    [dispatch]
  );

  const persistDevices = useCallback(
    (updatedDeviceId: string, updatedDeviceKind: MediaDeviceKind) => {
      const targetDevice = mediaDeviceEntities.find(
        (d) => d.deviceId === updatedDeviceId && d.kind === updatedDeviceKind
      );

      if (!targetDevice) {
        return;
      }

      safeLocalStorage.setItem(targetDevice.kind, JSON.stringify(targetDevice));

      const entries = Object.entries(activeMediaDevices);
      for (const [kind, deviceId] of entries) {
        if (deviceId && kind !== targetDevice.kind) {
          const activeTargetDevice = mediaDeviceEntities.find(
            (d) => d.deviceId === deviceId && d.kind === kind
          );

          if (activeTargetDevice) {
            safeLocalStorage.setItem(kind, JSON.stringify(activeTargetDevice));
          }
        }
      }
    },
    [activeMediaDevices, mediaDeviceEntities]
  );

  return {
    mediaDevices,
    activeMediaDevices,
    micPermissions: permissions.audioinput,
    camPermissions: permissions.videoinput,
    updateMediaDevices,
    persistDevices,
  };
};
